import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import api from "../../services/api";

// Words on the app's "Creating your video" screen. Saving English also translates it to
// Spanish, French and Portuguese on the server; any one language can be fixed by hand.
const STRINGS = [
  {
    key: "planStory.making",
    label: "Title",
    builtIn: { en: "Creating your video", es: "Creando tu video", fr: "Création de ta vidéo", pt: "Criando seu vídeo" },
  },
  {
    key: "planStory.waitPost",
    label: "Line under the title",
    builtIn: {
      en: "This usually takes about a minute",
      es: "Esto suele tardar más o menos un minuto",
      fr: "Ça prend en général environ une minute",
      pt: "Isso costuma levar cerca de um minuto",
    },
  },
];
const LANGS = [["es", "Spanish"], ["fr", "French"], ["pt", "Portuguese"]];

const ERROR_LOAD = "Couldn't load the text. Try again.";

export default function PlanStoryText() {
  const [messages, setMessages] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [editing, setEditing] = useState({});
  const [saving, setSaving] = useState(() => new Set());

  const load = useCallback(async () => {
    setLoadFailed(false);
    try {
      const { data } = await api.get("/app-config");
      if (!data || data.error) throw new Error(data?.error || "no data");
      setMessages(data.messages || {});
    } catch (err) {
      setLoadFailed(true);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const draftFor = (id, current) => (drafts[id] !== undefined ? drafts[id] : current);
  const setDraft = (id, v) => setDrafts((prev) => ({ ...prev, [id]: v }));
  const markSaving = (id, on) => setSaving((prev) => {
    const next = new Set(prev);
    if (on) next.add(id); else next.delete(id);
    return next;
  });

  // An English save also replaces the translations, so its language rows close and show the new text.
  const relatedIds = (id) => (id.includes("@") ? [id] : [id, ...LANGS.map(([lang]) => `${id}@${lang}`)]);

  const save = async (id, value, builtIn) => {
    const english = !id.includes("@");
    const isBuiltIn = english && value.trim() === builtIn;
    markSaving(id, true);
    try {
      const { data } = await api.put("/app-config/message", { key: id, value: isBuiltIn ? "" : value });
      setMessages(data?.messages || {});
      const ids = relatedIds(id);
      setDrafts((prev) => { const next = { ...prev }; ids.forEach((k) => delete next[k]); return next; });
      setEditing((prev) => { const next = { ...prev }; ids.forEach((k) => delete next[k]); return next; });
      if (isBuiltIn) toast.success("That's the built-in text, so the built-in translations stay.");
      else if (english && value.trim()) toast.success("Saved and translated. Open apps update now.");
      else toast.success("Saved. Open apps update now.");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not save");
    } finally {
      markSaving(id, false);
    }
  };

  const header = <h3 className="pt__subtitle pst__title">Video screen text</h3>;

  if (!messages) {
    return (
      <div className="pl pst">
        {header}
        {loadFailed ? (
          <div className="pst__failed">
            <p className="pt__error">{ERROR_LOAD}</p>
            <button className="pt__secondary" onClick={load}>Try again</button>
          </div>
        ) : (
          <p className="pt__note">Loading...</p>
        )}
      </div>
    );
  }

  return (
    <div className="pl pst">
      {header}
      <p className="pt__hint">
        The words members see while their plan video is being made. Save the English and the server
        translates it into Spanish, French and Portuguese. Saving the English again replaces any
        translation you fixed by hand. Leave a box empty and save to go back to the app's built-in text.
      </p>

      <div className="pl__groups">
        {STRINGS.map((s) => {
          const current = messages[s.key] || "";
          const draft = draftFor(s.key, current);
          const changed = draft.trim() !== current;
          const busy = saving.has(s.key);
          return (
            <div key={s.key} className="pl__group">
              <div className="pl__group-title">{s.label}</div>
              <div className="pl__row">
                <label className="pl__label" htmlFor={`pst-${s.key}`}>English</label>
                <div className="pl__control">
                  <input
                    id={`pst-${s.key}`}
                    className={`pl__input pst__input${changed ? " pl__input--edited" : ""}`}
                    type="text"
                    maxLength={200}
                    placeholder={s.builtIn.en}
                    value={draft}
                    onChange={(e) => setDraft(s.key, e.target.value)}
                  />
                  <button className="pt__primary" disabled={!changed || busy} onClick={() => save(s.key, draft, s.builtIn.en)}>
                    {busy ? "Saving..." : "Save"}
                  </button>
                </div>
                {!current && <div className="pt__note">Showing the built-in text.</div>}
              </div>

              {LANGS.map(([lang, name]) => {
                const id = `${s.key}@${lang}`;
                const value = messages[id] || "";
                const isEditing = !!editing[id];
                const d = draftFor(id, value);
                const rowBusy = saving.has(id) || busy;
                return (
                  <div key={lang} className="pl__row">
                    <div className="pl__label">{name}</div>
                    {isEditing ? (
                      <div className="pl__control">
                        <input
                          aria-label={`${s.label} in ${name}`}
                          className="pl__input pst__input"
                          type="text"
                          maxLength={200}
                          placeholder={s.builtIn[lang]}
                          value={d}
                          onChange={(e) => setDraft(id, e.target.value)}
                        />
                        <button className="pt__primary" disabled={d.trim() === value || rowBusy} onClick={() => save(id, d)}>
                          {saving.has(id) ? "Saving..." : "Save"}
                        </button>
                        <button
                          className="pt__link"
                          onClick={() => {
                            setDrafts((p) => { const next = { ...p }; delete next[id]; return next; });
                            setEditing((p) => ({ ...p, [id]: false }));
                          }}>
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="pl__control">
                        <span className="pst__text">{value || s.builtIn[lang]}</span>
                        {!value && <span className="pl__default">Built-in</span>}
                        <button className="pt__link" disabled={busy} onClick={() => setEditing((p) => ({ ...p, [id]: true }))}>Edit</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
